import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import Row from './Row';
import Dialog from './Dialog';
import Button from '../form/Button';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';

const Modes = Object.freeze({
  DEFAULT: 'default',
  BUTTON_MODAL: 'button-modal',
  LINK_MODAL: 'link-modal',
});

const VideoPreview = (props) => {
  const [isOpen, setIsOpen] = useState(false);

  const videoRef = useRef(null);
  const playerRef = useRef(null);

  const toggleModal = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        if (videoRef.current) {
          playerRef.current = videojs(videoRef.current);
        }
      });
    } else if (playerRef.current) {
      playerRef.current.dispose();
      playerRef.current = null;
    }
  }, [isOpen]);

  useEffect(() => {
    if (props.mode === Modes.DEFAULT && videoRef.current) {
      playerRef.current = videojs(videoRef.current);

      if (props.videoStartTime && props.videoEndTime) {
        const fixedStart = props.videoStartTime
          .match(/.{2}/g)
          .reduce((acc, value, index) => acc.concat(index ? ':' : '').concat(value), '');
        const fixedEnd = props.videoEndTime
          .match(/.{2}/g)
          .reduce((acc, value, index) => acc.concat(index ? ':' : '').concat(value), '');

        const startSeconds = moment.duration(fixedStart).asSeconds();
        const endSeconds = moment.duration(fixedEnd).asSeconds();

        playerRef.current.currentTime(startSeconds);
        playerRef.current.on('timeupdate', () => {
          if (playerRef.current.currentTime() >= endSeconds) {
            playerRef.current.pause();
            playerRef.current.currentTime(startSeconds);
          }
        });
      }
    }
    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [props.mode, props.videoStartTime, props.videoEndTime]);

  return (
    <>
      {props.mode === Modes.DEFAULT && (
        <video controls ref={videoRef} className="video-js vjs-default-skin vjs-16-9 vjs-big-play-centered">
          <source type="application/x-mpegURL" src={props.src} />
        </video>
      )}
      {[Modes.LINK_MODAL, Modes.BUTTON_MODAL].includes(props.mode) && (
        <>
          {props.mode === Modes.BUTTON_MODAL && <Button icon="video-camera" onClick={toggleModal} />}
          {props.mode === Modes.LINK_MODAL && (
            <span style={{ textDecoration: 'underline', color: '#1890ff', cursor: 'pointer' }} onClick={toggleModal}>
              {props.src}
            </span>
          )}
          <Dialog title="Video preview" actions={[]} isOpen={isOpen} onCancel={toggleModal} width={800}>
            {isOpen && (
              <Row>
                <video controls ref={videoRef} className="video-js vjs-default-skin vjs-16-9 vjs-big-play-centered">
                  <source type="application/x-mpegURL" src={props.src} />
                </video>
              </Row>
            )}
          </Dialog>
        </>
      )}
    </>
  );
};

VideoPreview.propTypes = {
  src: PropTypes.string.isRequired,
  videoStartTime: PropTypes.string,
  videoEndTime: PropTypes.string,
  width: PropTypes.number,
  mode: PropTypes.oneOf([Modes.DEFAULT, Modes.BUTTON_MODAL, Modes.LINK_MODAL]),
};

VideoPreview.defaultProps = {
  mode: Modes.DEFAULT,
};

export default VideoPreview;

export { Modes };
